import { Component, ElementRef, inject, Input, OnInit, ViewChild } from '@angular/core';
import { PatientService } from "../../../../core/services/patient.service";
import { FileAttachment } from "../../../../core/models/file-attachment.model";
import { AlertService } from "../../../../shared/alert/alert.service";
import { ConfirmActionModalComponent } from "../../../../shared/confirm-action-modal/confirm-action-modal.component";
import { DocumentViewerModalComponent } from "../../../../shared/document-viewer-modal/document-viewer-modal.component";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { FormControl, FormGroup } from "@angular/forms";
import { LogHelper } from "../../../../core/helpers/log.helper";
import { AuthService } from "../../../../core/services/auth.service";
import { RoleNames } from "../../../../core/constants/role-names";
import { Permissions } from "../../../../core/constants/permissions";

@Component({
  selector: 'app-patient-documents',
  templateUrl: './patient-documents.component.html',
  styleUrls: ['./patient-documents.component.scss']
})
export class PatientDocumentsComponent implements OnInit {
  @ViewChild('fileUploadInput') fileUploadInput: ElementRef;
  @ViewChild('confirmationModal') confirmationModal: ConfirmActionModalComponent;
  @ViewChild('documentViewer') documentViewer: DocumentViewerModalComponent;

  private readonly _patientService = inject(PatientService);
  private readonly _alertService = inject(AlertService);
  protected readonly _authService = inject(AuthService);

  @Input() patientTrialId: string;

  protected documents: FileAttachment[] = [];
  protected attachments: FileAttachment[] = [];

  dragOver = false;
  isUploading = false;
  removingDocumentId: string = null;
  filterForm: FormGroup = new FormGroup({
    order: new FormControl<string>('newest')
  });
  orderingOptions: { value: string, text: string }[] = [];

  // List of allowed extensions for upload (images and PDFs only)
  private allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];

  ngOnInit(): void {
    this.orderingOptions = [];
    this.orderingOptions.push({ value: 'newest', text: 'Newest' });
    this.orderingOptions.push({ value: 'oldest', text: 'Oldest' });
    this.orderingOptions.push({ value: 'filename', text: 'Filename' });

    this.loadDocuments();
  }

  loadDocuments() {
    this._patientService.getPatientDocuments(this.patientTrialId).subscribe({
      next: documents => {
        this.documents = documents;
        this.onFilterChange(this.filterForm.value.order);
      },
      error: error => {
        this._alertService.showErrorAlert(error);
      }
    });
  }

  onFilterChange(filterValue) {
    switch (filterValue) {
      case 'newest':
        this.documents.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime());
        break;
      case 'oldest':
        this.documents.sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime());
        break;
      case 'filename':
        this.documents.sort((a, b) => a.originalFileName.localeCompare(b.originalFileName));
        break;
    }
  }

  onOpenFileBrowser(event: any) {
    event.preventDefault();
    const element = this.fileUploadInput.nativeElement as HTMLElement;
    element.click();
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.documents, event.previousIndex, event.currentIndex);
  }

  onFileSelected(event) {
    if (event?.target?.files.length) {
      const files = Array.from(event.target.files as FileList) as File[];
      const notAllowed = files.filter(file => {
        const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
        return !this.allowedExtensions.includes(ext);
      });
      if (notAllowed.length > 0) {
        this._alertService.showWarningAlert('Only PDF and image files (JPG, PNG, GIF, BMP, WEBP) are allowed.');
        event.target.value = '';
        return;
      }
      this.isUploading = true;
      let uploadCount = files.length;
      let completed = 0;
      for (let file of files) {
        // Check that the file hasn't already been uploaded
        const existing = this.documents.find(a => a.originalFileName === file.name);
        if (existing !== null && existing !== undefined) {
          this._alertService.showWarningAlert('A document with the name ' + file.name + ' exists for this patient.');
          completed++;
          if (completed === uploadCount) this.isUploading = false;
          continue;
        }

        let fr = new FileReader();

        fr.onload = (fileLoadEvent: any) => {
          // Decode base64 data
          const base64Data = fileLoadEvent.target.result.split(',')[1];
          const byteArray = this.convertToByteArray(window.atob(base64Data));
          const fileBlob = new Blob(byteArray, { type: file.type });
          this._patientService.uploadPatientDocument(this.patientTrialId, file.name, 0, fileBlob).subscribe({
            next: (documents) => {
              //this.documents = documents;
              completed++;
              this.onFilterChange(this.filterForm.value.order);
              if (completed === uploadCount) {
                this.loadDocuments();
                this.isUploading = false;
              }
            },
            error: error => {
              this._alertService.showErrorAlert(error);
              completed++;
              if (completed === uploadCount) this.isUploading = false;
            }
          });
        };
        fr.readAsDataURL(file);
      }
      event.target.value = '';
    }
  }

  onRemoveDocumentConfirmed(documentId: string) {
    this.removingDocumentId = documentId;
    this._patientService.removePatientDocument(this.patientTrialId, documentId).subscribe({
      next: documents => {
        this.documents = documents;
        this.onFilterChange(this.filterForm.value.order);
        this.removingDocumentId = null;
      },
      error: error => {
        this._alertService.showErrorAlert(error);
        this.removingDocumentId = null;
      }
    });
  }

  convertToByteArray(input) {
    const sliceSize = 512;
    const bytes: Uint8Array[] = [];

    for (let offset = 0; offset < input.length; offset += sliceSize) {
      const slice = input.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      bytes.push(byteArray);
    }

    return bytes;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragOver = false;
    if (event.dataTransfer && event.dataTransfer.files.length > 0) {
      const fileEvent = { target: { files: event.dataTransfer.files } };
      this.onFileSelected(fileEvent);
    }
  }

  onViewDocument(documentId: string) {
    const doc = this.documents.find(d => d.id === documentId);
    if (!doc) return;

    // Check if file can be previewed
    if (!this.documentViewer.canPreview(doc.originalFileName)) {
      // Fall back to download for unsupported types
      this.onDownloadDocument(documentId);
      return;
    }

    this._patientService.downloadPatientDocument(this.patientTrialId, documentId)
      .subscribe({
        next: (fileBlob: Blob) => {
          this.documentViewer.show(fileBlob, doc.originalFileName);
        },
        error: error => {
          LogHelper.log(error);
          this._alertService.showErrorAlert(error);
        }
      });
  }

  onDownloadDocument(documentId: string) {
    const doc = this.documents.find(d => d.id === documentId);
    if (!doc) return;
    this._patientService.downloadPatientDocument(this.patientTrialId, documentId)
      .subscribe({
        next: (fileBlob: Blob) => {
          const url = window.URL.createObjectURL(fileBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = doc.originalFileName || 'download';
          a.target = '_blank';
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        },
        error: error => {
          LogHelper.log(error);
          this._alertService.showErrorAlert(error);
        }
      });
  }

  canPreviewDocument(fileName: string): boolean {
    const previewableExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'];
    const extension = fileName.substring(fileName.lastIndexOf('.')).toLowerCase();
    return previewableExtensions.includes(extension);
  }

  protected readonly RoleNames = RoleNames;
  protected readonly Permissions = Permissions;
}
